import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUploadCloud } from 'react-icons/fi';
import './styles.css';

// Contexts
import { GeneralContext, ModalContext } from '~/contexts';

// Components
import Admin from '~/components/Admin';
import { LoadingSingle } from '~/components/Default/Loading';

// Services
import charges from '~/services/charges';
import numbers from '~/utils/numbers';

type ChargeRow = {
  id: string;
  unitName: string;
  unitId?: number;
  month: number; // 1-12
  value: any;
};

function defaultYear() {
  return new Date().getFullYear();
}

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const AdminUploads: React.FC<any> = () => {
  const navigate = useNavigate();

  const { user, units, unit, logged } = useContext(GeneralContext);
  const Modal = useContext(ModalContext);

  const [ready, setReady] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // filtros
  const [unitQuery, setUnitQuery] = useState<string>('');
  const [year, setYear] = useState<number>(defaultYear());
  const [month, setMonth] = useState<number>(new Date().getMonth());
  const [type, setType] = useState<string>('ercap');

  // listagem (via GET /charges)
  const [rows, setRows] = useState<ChargeRow[]>([]);

  const normalizeText = (s: any) =>
    String(s ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const selectedUnit = useMemo(() => {
    // Seleção "silenciosa" apenas quando o texto for um match exato (para permitir upload)
    const q = normalizeText(unitQuery);
    if (!q) return null;
    const list = Array.isArray(units) ? units : [];
    const exact = list.find((u: any) => normalizeText(u?.name) === q);
    return exact || null;
  }, [units, unitQuery]);

  const visibleRows = useMemo(() => {
    const q = normalizeText(unitQuery);
    if (!q) return rows;
    return rows.filter((r) => normalizeText(r.unitName).includes(q));
  }, [rows, unitQuery]);

  async function loadCharges() {
    if (!user?.token) return;

    setLoading(true);
    setError(null);

    const result = await charges.getCharges.get(user, {
      year,
      month: month + 1,
      type,
      unitId: selectedUnit?.id ? Number(selectedUnit.id) : undefined,
    });

    if (!result?.valid) {
      setRows([]);
      setError(result?.message || 'Erro ao carregar charges.');
      setLoading(false);
      return;
    }

    const list: Array<{ month: number; value: any; unitName?: string; unitId?: number }> = Array.isArray(result?.data) ? result.data : [];
    setRows(
      list.map((el, idx) => ({
        id: `charge_${year}_${month + 1}_${type}_${String(el?.unitId ?? '')}_${idx}`,
        unitName: String(el?.unitName || '').trim() || '-',
        unitId: el?.unitId != null ? Number(el.unitId) : undefined,
        month: Number(el.month),
        value: el.value,
      }))
    );
    setLoading(false);
  }

  function openUploadModal() {
    Modal.setModalOpen(false);
    Modal.setModalClass('madminUpload');
    Modal.setModalTitle('Realizar importação');
    Modal.setModalBody(
      <UploadModalBody
        user={user}
        unit={selectedUnit}
        year={year}
        month={month}
        onChangeYear={(y: number) => setYear(y)}
        onChangeMonth={(m: number) => setMonth(m)}
        type={type}
        onChangeType={(t: string) => setType(t)}
        onSuccess={() => {
          // após upload, recarrega a listagem do GET /charges
          loadCharges();
        }}
      />
    );
    Modal.setModalOpen(true);
  }

  useEffect(() => {
    if (!logged || !user?.token) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.role !== 'admin') {
      navigate('/404', { replace: true });
      return;
    }

    setReady(true);
  }, [logged, user, unit, navigate, unitQuery]);

  useEffect(() => {
    if (!ready) return;
    loadCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, selectedUnit?.id, year, month, type]);

  return (
    <Admin
      pageTitle="Administrador - Encargos e Taxas"
      title="Encargos e Taxas"
      subtitle="Importação e consulta"
      classMain="adm_uploads"
    >
      {ready ? (
        <div className="uploadsContent">
          <div className="header">
            <div className="filters">
              <div className="filter">
                <label>Unidade</label>
                <div className="unitInput">
                  <input
                    type="text"
                    placeholder="Digite o nome da unidade..."
                    value={unitQuery}
                    onChange={(e) => {
                      setUnitQuery(e.target.value);
                    }}
                    autoComplete="off"
                    disabled={!Array.isArray(units) || units.length === 0}
                  />
                </div>
              </div>

              <div className="filter">
                <label>Período</label>
                <div className="period">
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {MONTHS.map((m, idx) => (
                      <option key={`m_${idx}`} value={idx}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                      <option key={`y_${y}`} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="filter">
                <label>Tipo</label>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                  {UPLOAD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button type="button" className="btnUpload" onClick={openUploadModal}>
                <FiUploadCloud size={16} />
                <span>Importar arquivo</span>
              </button>
          </div>

          <div className="tableWrap">
            <table className="table table--uploads">
              <thead>
                <tr>
                  <th>Unidade</th>
                  <th>Mês</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="empty">Carregando...</td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={3} className="empty">{error}</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="empty">
                      Nenhum dado encontrado
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.unitName}</td>
                      <td>{MONTHS[(r.month || 1) - 1] ?? String(r.month)}</td>
                      <td>{(() => {
                        const n = Number(r.value);
                        if (Number.isFinite(n)) return numbers.money(n, true);
                        return String(r.value ?? '-');
                      })()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <LoadingSingle />
      )}
    </Admin>
  );
};

const UPLOAD_TYPES: Array<{ value: string; label: string }> = [
  { value: 'ercap', label: 'Taxa ERCAP' },
  { value: 'nuclear_energy_settlement', label: 'Liquidação de Energia Nuclear' },
  { value: 'fees', label: 'Taxa CCEE' },
  { value: 'backup_power', label: 'Energia de Reserva' },
  { value: 'financial_guarantee', label: 'Garantia Financeira' },
];

const UploadModalBody: React.FC<any> = (props) => {
  const { setModalOpen } = useContext(ModalContext);

  const [type, setType] = useState<string>(props.type || UPLOAD_TYPES[0].value);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState<boolean>(false);

  const normalizeMsg = (s: any) =>
    String(s ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const isExistingDataMessage = (msg: any) => {
    const m = normalizeMsg(msg);
    return m.includes('dados ja existentes');
  };

  async function doUpload(overwrite: boolean) {
    // sempre limpa mensagens/passo antes de submeter
    setError(null);
    setSuccess(null);
    if (!overwrite) setConfirmOverwrite(false);

    if (!type) {
      setError('Selecione um tipo de arquivo antes de enviar o arquivo.');
      return;
    }

    if (props?.year == null) {
      setError('Selecione um ano antes de enviar o arquivo.');
      return;
    }

    // month é 0-11; janeiro é 0 (não pode ser tratado como falsy)
    if (props?.month == null) {
      setError('Selecione um mês antes de enviar o arquivo.');
      return;
    }

    if (!file) {
      setError('Selecione um arquivo.');
      return;
    }

    setLoading(true);

    const result = await charges.uploadCharges.upload(props.user, {
      file,
      year: props.year,
      month: (props.month ?? 0) + 1,
      type,
      overwrite,
    });

    setLoading(false);

    if (result?.valid) {
      setConfirmOverwrite(false);
      setError(null);
      props.onSuccess?.(result);
      setSuccess('Upload processado com sucesso.');
      setTimeout(() => {
        setModalOpen(false);
      }, 6000);
    } else {
      const msg = result?.message || result?.data?.message;
      if (!overwrite && isExistingDataMessage(msg)) {
        // mostra confirmação no lugar do seletor de arquivo
        setError(null);
        setSuccess(null);
        setConfirmOverwrite(true);
        return;
      }
      setConfirmOverwrite(false);
      setError(msg || 'Falha ao processar upload.');
    }
  }

  return (
    <div className="uploadModal">
      <div className="row">
        <div className="field">
          <label>Ano</label>
          <div className="period">
            <select
              value={props.month}
              onChange={(e) => props.onChangeMonth?.(Number(e.target.value))}
              disabled={loading || Boolean(success) || confirmOverwrite}
            >
              {MONTHS.map((m, idx) => (
                <option key={`mm_${idx}`} value={idx}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={props.year}
              onChange={(e) => props.onChangeYear?.(Number(e.target.value))}
              disabled={loading || Boolean(success) || confirmOverwrite}
            >
              {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                <option key={`yy_${y}`} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label>Tipo do arquivo</label>
          <select value={type} onChange={(e) => {
            setType(e.target.value);
            props.onChangeType?.(e.target.value);
          }} disabled={loading || Boolean(success) || confirmOverwrite}>
            {UPLOAD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label>Arquivo</label>
          {confirmOverwrite ? (
            <div className="stageBox stageBox--info">
              <div className="stageBox__content">
                <div className="stageBox__title">
                  Já existem dados cadastrados para esse tipo, mês e ano.
                </div>
                <div className="stageBox__sub">
                  Deseja sobrescrevê-los?
                </div>
                <div className="confirmActions">
                  <button
                    type="button"
                    className="btnSecondary"
                    onClick={() => {
                      setConfirmOverwrite(false);
                      setError(null);
                      setSuccess(null);
                      // volta para a etapa anterior (arquivo) sem perder o arquivo selecionado
                    }}
                    disabled={loading}
                  >
                    Não
                  </button>
                  <button
                    type="button"
                    className="btnPrimary"
                    onClick={() => doUpload(true)}
                    disabled={loading}
                  >
                    Sim, sobrescrever
                  </button>
                </div>
              </div>
            </div>
          ) : success ? (
            <div className="stageBox stageBox--success">
              <div className="stageBox__content">
                <div className="stageBox__title">{success}</div>
                <div className="stageBox__sub">Você pode fechar este modal.</div>
              </div>
            </div>
          ) : error ? (
            <div className="stageBox stageBox--error">
              <div className="stageBox__content">
                <div className="stageBox__title">Falha ao processar upload</div>
                <div className="stageBox__sub">{error}</div>
                <div className="confirmActions">
                  <button
                    type="button"
                    className="btnSecondary"
                    onClick={() => {
                      setError(null);
                      setSuccess(null);
                      setConfirmOverwrite(false);
                    }}
                    disabled={loading}
                  >
                    Voltar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="drop drop--large">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={loading}
              />
              <div className="hint">
                <div className="title">{file ? file.name : 'Escolher um arquivo'}</div>
                <div className="sub">Formato: CSV</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmOverwrite ? null : (
        <footer className="footer">
          <button type="button" className="btnSecondary" onClick={() => setModalOpen(false)} disabled={loading || !file}>
            Fechar
          </button>
          {success || error ? null : (
            <button
              type="button"
              className="btnPrimary"
              onClick={() => doUpload(false)}
              disabled={loading || !file}
            >
              {loading ? 'Processando...' : 'Processar Arquivo'}
            </button>
          )}
        </footer>
      )}
    </div>
  );
};

export default AdminUploads;

